import React from 'react';
import { Box, Grid, Typography } from '@mui/material';
import DeviceLoadingSkeleton from '../../devices/shared-ui/deviceLoadingSkeleton';
import { formatDeviceMetric } from '../../../utils/formatters';
import {
  useEnergyConsumerUtilityDetailQuery,
  useEnergyConsumerUtilityDetailUpdatesSubscription,
} from '../../../../types/generated-types';
import { PanelFrame, ViewMode } from '../../devices/shared-ui/panel-frame';
import moment from 'moment';
import { DeviceInformation } from '../../devices/shared-ui/device-info';
import { updateCacheFromSubscriptionEvent } from '../../../../helpers/subscriptionUtils';
import Divider from '@mui/material/Divider';
import { EnergySwitchPanelCircuitComponent } from '../../devices/virtual-power-plant/energy-switch-panel-circuit';
import { EnergyMeterComponent } from '../../devices/virtual-power-plant/energy-meter';
import { EnergyInverterComponent } from '../../devices/virtual-power-plant/energy-inverter';
import { EnergyConverterComponent } from '../../devices/virtual-power-plant/energy-converter';
import Stack from '@mui/material/Stack';

interface EnergyConsumerUtilityProps {
  id: string;
}

export const EnergyConsumerUtilityComponent: React.FC<
  EnergyConsumerUtilityProps
> = ({ id }) => {
  const [participant, setParticipant] = React.useState<any>();
  const { data, loading, error } = useEnergyConsumerUtilityDetailQuery({
    variables: { id },
  });

  useEnergyConsumerUtilityDetailUpdatesSubscription({
    variables: { id },
    fetchPolicy: 'no-cache',
    onData: updateCacheFromSubscriptionEvent,
  });

  React.useEffect(() => {
    if (data?.energyConsumerUtilityById) {
      setParticipant(data.energyConsumerUtilityById);
    }
  }, [data]);

  if (loading) return <DeviceLoadingSkeleton size="large" />;
  if (error)
    return (
      <Typography color="error">Error loading participant data</Typography>
    );
  if (!participant) return null;

  const participantInfo: DeviceInformation[] = [
    {
      'Utility Name': participant.name || 'Unnamed Utility',
      Location: participant.location || 'Unknown',
      'Last Updated': moment(participant.timestamp).fromNow(),
      'Power State': participant.powerState?.value ? 'On' : 'Off',
      'Connection Status': participant.connected?.value
        ? 'Connected'
        : 'Disconnected',
      'Last Connected': moment(participant.lastConnectedAt?.value).fromNow(),
      'Tariff Rate': formatDeviceMetric({
        value: participant.tariffRateSell?.value ?? undefined,
        units: participant.tariffRateSell?.units ?? undefined,
      }),
      'Grid Frequency': formatDeviceMetric({
        value: participant.gridFrequency?.value ?? undefined,
        units: participant.gridFrequency?.units ?? undefined,
      }),
      'Interconnection Capacity': formatDeviceMetric({
        value: participant.interconnectionCapacity?.value ?? undefined,
        units: participant.interconnectionCapacity?.units ?? undefined,
      }),
      'Export Allowance': formatDeviceMetric({
        value: participant.exportAllowance?.value ?? undefined,
        units: participant.exportAllowance?.units ?? undefined,
      }),
      'Contract Status': participant.contractStatus?.value
        ? 'Active'
        : 'Inactive',
    },
  ];

  return (
    <PanelFrame
      title="Energy Consumer Utility"
      iconType="material-symbols:power"
      panel={participant}
      infoEntries={participantInfo}
      viewMode={ViewMode.NORMAL}
      editingEnabled={false}
    >
      <Box sx={{ p: 2 }}>
        <Stack
          direction={'row'}
          justifyContent={'space-between'}
          divider={<Divider orientation="vertical" flexItem />}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Utility Status
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={3}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Power State
                  </Typography>
                  <Typography variant="body1">
                    {participant.powerState?.value ? 'On' : 'Off'}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Connection Status
                  </Typography>
                  <Typography variant="body1">
                    {participant.connected?.value
                      ? 'Connected'
                      : 'Disconnected'}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Last Connected
                  </Typography>
                  <Typography variant="body1">
                    {moment(participant.lastConnectedAt?.value).fromNow()}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Contract Status
                  </Typography>
                  <Typography variant="body1">
                    {participant.contractStatus?.value ? 'Active' : 'Inactive'}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Grid Metrics
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={3}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Grid Frequency
                  </Typography>
                  <Typography variant="body1">
                    {formatDeviceMetric({
                      value: participant.gridFrequency?.value ?? undefined,
                      units: participant.gridFrequency?.units ?? undefined,
                    })}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Interconnection Capacity
                  </Typography>
                  <Typography variant="body1">
                    {formatDeviceMetric({
                      value:
                        participant.interconnectionCapacity?.value ?? undefined,
                      units:
                        participant.interconnectionCapacity?.units ?? undefined,
                    })}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Export Allowance
                  </Typography>
                  <Typography variant="body1">
                    {formatDeviceMetric({
                      value: participant.exportAllowance?.value ?? undefined,
                      units: participant.exportAllowance?.units ?? undefined,
                    })}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Tariff Rate
                  </Typography>
                  <Typography variant="body1">
                    {formatDeviceMetric({
                      value: participant.tariffRateSell?.value ?? undefined,
                      units: participant.tariffRateSell?.units ?? undefined,
                    })}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          {participant.energySwitchPanelCircuitId ? (
            <EnergySwitchPanelCircuitComponent
              id={participant.energySwitchPanelCircuitId}
            />
          ) : null}
          {participant.meterId ? (
            <EnergyMeterComponent id={participant.meterId} />
          ) : null}
          {participant.inverterId ? (
            <EnergyInverterComponent id={participant.inverterId} />
          ) : null}
          {participant.converterId ? (
            <EnergyConverterComponent id={participant.converterId} />
          ) : null}
        </Stack>
      </Box>
    </PanelFrame>
  );
};
